/**
 * HC-8: "The form validates before spending a provider call: Luhn check on the
 * card number, a real future expiry, correct CVV length for the detected brand."
 *
 * Every function here operates on a value held in a React state variable and
 * never on a form field — HC-6 keeps card inputs out of any form submission.
 */

const BRANDS = [
  { brand: 'amex', pattern: /^3[47]/, lengths: [15], cvcLength: 4, gaps: [4, 10] },
  { brand: 'diners', pattern: /^3(?:0[0-5]|[68])/, lengths: [14, 16, 19], cvcLength: 3, gaps: [4, 10] },
  { brand: 'visa', pattern: /^4/, lengths: [13, 16, 19], cvcLength: 3, gaps: [4, 8, 12] },
  { brand: 'mastercard', pattern: /^(5[1-5]|2[2-7])/, lengths: [16], cvcLength: 3, gaps: [4, 8, 12] },
  { brand: 'discover', pattern: /^(6011|64[4-9]|65)/, lengths: [16, 19], cvcLength: 3, gaps: [4, 8, 12] },
  { brand: 'jcb', pattern: /^35(2[89]|[3-8])/, lengths: [16, 17, 18, 19], cvcLength: 3, gaps: [4, 8, 12] },
];

const DEFAULT_BRAND = { brand: null, lengths: [16, 19], cvcLength: 3, gaps: [4, 8, 12] };

export const digitsOnly = (value) => String(value ?? '').replace(/\D/g, '');

export function detectBrand(cardNumber) {
  const digits = digitsOnly(cardNumber);
  return BRANDS.find((b) => b.pattern.test(digits)) ?? DEFAULT_BRAND;
}

/** Groups digits the way the brand prints them — 4-6-5 for Amex, 4-4-4-4 otherwise. */
export function formatCardNumber(value) {
  const { gaps, lengths } = detectBrand(value);
  const digits = digitsOnly(value).slice(0, Math.max(...lengths));
  let out = '';
  for (let i = 0; i < digits.length; i += 1) {
    if (gaps.includes(i)) out += ' ';
    out += digits[i];
  }
  return out;
}

/**
 * The Luhn checksum. It catches a mistyped digit before we spend a provider call
 * and, more importantly, before the customer is told their card was declined for
 * what is really a typo.
 */
export function passesLuhn(cardNumber) {
  const digits = digitsOnly(cardNumber);
  if (digits.length < 12) return false;

  let sum = 0;
  let double = false;
  for (let i = digits.length - 1; i >= 0; i -= 1) {
    let digit = Number(digits[i]);
    if (double) {
      digit *= 2;
      if (digit > 9) digit -= 9;
    }
    sum += digit;
    double = !double;
  }
  return sum % 10 === 0;
}

export function validateCardNumber(cardNumber) {
  const digits = digitsOnly(cardNumber);
  if (!digits) return 'Enter your card number.';
  const { lengths } = detectBrand(digits);
  if (digits.length < Math.min(...lengths)) return 'That card number looks too short.';
  if (!lengths.includes(digits.length) || !passesLuhn(digits)) {
    // HC-14: never blames the customer — a typo is the likely cause.
    return 'Please check the card number and try again.';
  }
  return null;
}

/** Accepts "12/28", "1228" or "12 / 2028" — people type all three. */
export function parseExpiry(value) {
  const digits = digitsOnly(value);
  if (digits.length < 3) return null;
  const month = Number(digits.slice(0, 2));
  const yearPart = digits.slice(2, 6);
  const year = yearPart.length <= 2 ? 2000 + Number(yearPart) : Number(yearPart);
  if (!month || month < 1 || month > 12) return null;
  return { month, year };
}

export function formatExpiry(value) {
  const digits = digitsOnly(value).slice(0, 6);
  if (digits.length <= 2) return digits;
  return `${digits.slice(0, 2)} / ${digits.slice(2)}`;
}

export function validateExpiry(value, { now = () => new Date() } = {}) {
  const parsed = parseExpiry(value);
  if (!parsed) return 'Enter the expiry as MM / YY.';

  const today = now();
  // A card is valid through the last day of its expiry month.
  const lastDay = new Date(parsed.year, parsed.month, 0, 23, 59, 59, 999);
  if (lastDay < today) return 'That card has expired.';
  if (parsed.year > today.getFullYear() + 25) return 'Please check the expiry date.';
  return null;
}

export function validateCvc(cvc, cardNumber) {
  const digits = digitsOnly(cvc);
  const { cvcLength, brand } = detectBrand(cardNumber);
  if (!digits) return 'Enter the security code.';
  if (digits.length !== cvcLength) {
    return brand === 'amex'
      ? 'American Express security codes are 4 digits, on the front of the card.'
      : `The security code is ${cvcLength} digits, on the back of the card.`;
  }
  return null;
}

export const BRAND_LABELS = {
  visa: 'Visa', mastercard: 'Mastercard', amex: 'American Express',
  discover: 'Discover', jcb: 'JCB', diners: 'Diners Club',
};


/**
 * A phone number, checked on shape rather than on plausibility.
 *
 * Deliberately permissive, and deliberately mirroring the server's rule: a
 * validator that is stricter here than there rejects a real customer's real
 * number at the one screen where that costs a sale. Seven digits is the floor
 * because nothing shorter is a phone number anywhere; fifteen is E.164's
 * ceiling.
 */
export function validatePhone(value) {
  const digits = String(value ?? '').replace(/\D/g, '');
  if (!digits) return 'Enter your phone number';
  if (digits.length < 7 || digits.length > 15) return 'Check this phone number';
  return null;
}

/** Keeps what people actually type — digits, spaces, dashes, brackets, a plus. */
export const formatPhone = (value) => String(value ?? '').replace(/[^\d+\-() ]/g, '').slice(0, 24);
